import { useState, useEffect } from 'react';
import { Switch, Route } from 'react-router-dom';
import PropTypes from 'prop-types';
import { getTenantConfig, getTenantNav, isMobileApp, ROUTES } from 'Utils';
import * as analytics from 'Utils/analytics';
import Interests from 'Pages/profile/Interests';
import Library from 'Pages/profile/Library';
import Settings from 'Pages/profile/Settings';
import { withLogin, withToaster } from 'Components/hocs';
import ProgressBar from 'Components/ProgressBar';
import RouterNav from 'Components/profile/RouterNav';

const { nestedRoutes } = ROUTES.PAGE_PROFILE;
const { EVENTS } = analytics;
const PROFILE_ROUTES = [
  {
    icon: 'bookmark',
    key: 'library',
  },
  // {
  //   icon: 'filter',
  //   key: 'interests',
  // },
  {
    icon: 'settings',
    key: 'settings',
  },
];

const Profile = (props) => {
  const {
    applicationConfig,
    country,
    location,
    language,
    strings,
    transitionTo,
    uim,
  } = props;
  const [profile, setProfile] = useState({});
  const [hasProfile, setHasProfile] = useState(false);
  const [hasNative, setHasNative] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const isMobile = isMobileApp(applicationConfig);
  const routes = isMobile
    ? PROFILE_ROUTES.filter(({ key }) => key === 'settings')
    : PROFILE_ROUTES;
  // New routes once Interests rail is available:
  // let routes;
  // if (getTenantConfig().interestsOnProfile) {
  //   routes = isMobile
  //     ? PROFILE_ROUTES.filter(({ key }) => key !== 'library')
  //     : PROFILE_ROUTES;
  // } else {
  //   routes = isMobile
  //     ? PROFILE_ROUTES.filter(({ key }) => key === 'settings')
  //     : PROFILE_ROUTES.filter(({ key }) => key !== 'interests');
  // }
  const { email, firstName, lastName, avatar } = profile;

  const onPageLoad = () => {
    if (location.pathname === ROUTES.PAGE_PROFILE.url) {
      const normalizedRoute = (url) => `${location.pathname}${url}`;
      // If mobile app links to '/profile', forward to '/profile/settings'
      if (isMobile) transitionTo(normalizedRoute(nestedRoutes.settings.url));
      // Otherwise forward to '/profile/library'
      else transitionTo(normalizedRoute(nestedRoutes.library.url));
    }
  };

  const loadProfile = async () => {
    const jwt = await uim.getJWT();
    const profile = jwt && (await uim.parseJWT(jwt));
    const avatar = profile?.big_avatar_link ?? '';
    const {
      email,
      first_name: firstName,
      last_name: lastName,
    } = uim.getProfileValues();
    const hasNative = await uim.hasEmailLogin();
    const isVerified = profile?.verification_status === 'verified' ?? false;

    setProfile({
      avatar,
      email,
      firstName,
      lastName,
    });
    setHasProfile(true);
    setHasNative(hasNative);
    setIsVerified(isVerified);
  };

  const renderNav = (alignment = 'vertical') => (
    <RouterNav
      alignment={alignment}
      onClick={(key) => {
        analytics.event({ action: EVENTS.selectProfileView, label: key });
      }}
      parentRoute={ROUTES.PAGE_PROFILE}
      routes={routes}
      strings={strings}
    />
  );

  useEffect(() => {
    document.body.classList.add('isProfile');
    onPageLoad();
    loadProfile();
    return () => document.body.classList.remove('isProfile');
  }, []);

  if (!hasProfile) return <ProgressBar />;

  return (
    <>
      {!isMobile && getTenantNav(country, language)}
      <div className="sidebar-view">
        <div className="sidebar">
          <div className="sidebar__header">
            {avatar ? (
              <div
                className="avatar"
                style={{ backgroundImage: `url(${avatar})` }}
              />
            ) : (
              <div className="avatar">
                {/* prettier-ignore */}
                <div className="avatar__text">{`${firstName.charAt(0)}${lastName.charAt(0)}`}</div>
              </div>
            )}
            {/* We can remove old translation key - profileHeading */}
            <h1 className="sidebar__heading">{`${firstName} ${lastName}`}</h1>
            <h2 className="sidebar__subheading" title={email}>
              {email}
            </h2>
          </div>

          {/* Desktop navigation */}
          {renderNav()}
        </div>

        <div className="sidebar-view__content">
          {/* Mobile navigation */}
          <div className="sidebar-view__header mobile__header">
            {renderNav('horizontal')}
          </div>

          <Switch>
            <Route path={`${ROUTES.PAGE_PROFILE.url}/library`}>
              <Library {...props} />
            </Route>
            <Route path={`${ROUTES.PAGE_PROFILE.url}/interests`}>
              <Interests {...props} />
            </Route>
            <Route path={`${ROUTES.PAGE_PROFILE.url}/settings`}>
              <Settings
                {...props}
                hasNative={hasNative}
                hasProfile={hasProfile}
                isVerified={isVerified}
                name={`${firstName} ${lastName}`}
              />
            </Route>
          </Switch>
        </div>
      </div>
    </>
  );
};

Profile.propTypes = {
  applicationConfig: PropTypes.object.isRequired,
  country: PropTypes.string.isRequired,
  language: PropTypes.string.isRequired,
  location: PropTypes.object.isRequired,
  strings: PropTypes.object.isRequired,
  transitionTo: PropTypes.func.isRequired,
  uim: PropTypes.object.isRequired,
};

export default withToaster(withLogin(Profile));
